

/* eslint-disable */
import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';

import { VenueService } from '../../../shared/services/venue.service';
import { AccountService } from '../../../modules/admin/settings/account/account.service'; // Import AccountsService
import { BehaviorSubject, Observable, map, switchMap, of } from 'rxjs';

// ----------------------
// Static exports (needed for api.ts mock)
// ----------------------
export const defaultNavigation: FuseNavigationItem[] = [
    {
        id   : 'overview',
        title: 'Overview',
        type : 'group',
        children: [
            {
                id   : 'company',
                title: 'Company',
                type : 'basic',
                icon : 'heroicons_outline:building-office-2',
                link : '/company'
            },
            {
                id   : 'venues',
                title: 'Venues',
                type : 'collapsable',
                icon : 'location_city',
                link : '/venues',
                children: [] // gets replaced dynamically
            },
            {
                id   : 'courts',
                title: 'Courts',
                type : 'basic',
                icon: 'sports_tennis',
                link : '/courts'
            },
            {
                id   : 'bookings',
                title: 'Bookings',
                type : 'basic',
                icon : 'mat_outline:bar_chart',
                link : '/bookings'
            },
            {
                id   : 'users',
                title: 'Users',
                type : 'collapsable',
                icon : 'mat_outline:people',
                children: [
                    {
                        id: 'users-players',
                        title: 'Players',
                        type: 'basic',
                        icon: 'sports_esports',
                        link: '/users/players'
                    },
                    {
                        id: 'users-admins',
                        title: 'Admins',
                        type: 'basic',
                        link: '/users/admins',
                        icon: 'admin_panel_settings'
                    }
                ]
            },
             {
                id   : 'feedback',
                title: "User's Feedback",
                type : 'basic',
                icon: 'mat_outline:feedback',
                link : '/feedback'
            },
        ]
    }
];

export const compactNavigation: FuseNavigationItem[] = [
    {
        id   : 'example',
        title: 'Example',
        type : 'basic',
        icon : 'heroicons_outline:chart-pie',
        link : '/example'
    }
];

export const futuristicNavigation: FuseNavigationItem[] = [
    {
        id   : 'example',
        title: 'Example',
        type : 'basic',
        icon : 'heroicons_outline:chart-pie',
        link : '/example'
    }
];

export const horizontalNavigation: FuseNavigationItem[] = [
    {
        id   : 'example',
        title: 'Example',
        type : 'basic',
        icon : 'heroicons_outline:chart-pie',
        link : '/example'
    }
];

// ----------------------
// Dynamic service for runtime
// ----------------------
@Injectable({
  providedIn: 'root',
})
export class NavigationData {
  private venueService = inject(VenueService);
  private accountsService = inject(AccountService);

  private _navigation = new BehaviorSubject<FuseNavigationItem[]>(defaultNavigation);
  navigation$ = this._navigation.asObservable();

  constructor() {
    this.loadNavigationBasedOnRole();
  }

  private loadNavigationBasedOnRole(): void {
    this.accountsService.getCurrentUser().pipe(
      switchMap((response: any) => {
        // Extract user data from the API response
        const userData = response?.data;
        const userRole = userData?.role;
        
        console.log('User Role:', userRole); // For debugging
        
        // Check if user is staff
        if (userRole === 'staff') {
          // For staff role, remove venues from navigation
          return this.createNavigationWithoutVenues();
        } else {
          // For other roles (admin, manager, etc.), load venues normally
          return this.loadVenuesWithNavigation();
        }
      })
    ).subscribe({
      next: (navigation: FuseNavigationItem[]) => {
        this._navigation.next(navigation);
      },
      error: (error) => {
        console.error('Error loading navigation:', error);
        // Fallback to default navigation without venues on error
        this.createNavigationWithoutVenues().subscribe(fallbackNav => {
          this._navigation.next(fallbackNav);
        });
      }
    });
  }

  private createNavigationWithoutVenues(): Observable<FuseNavigationItem[]> {
    // Create a deep copy of defaultNavigation without the venues item
    const navWithoutVenues = JSON.parse(JSON.stringify(defaultNavigation));
    
    // Filter out the venues item from children
    navWithoutVenues[0].children = navWithoutVenues[0].children.filter(
      (item: FuseNavigationItem) => item.id !== 'venues'
    );

    return of(navWithoutVenues);
  }

  private loadVenuesWithNavigation(): Observable<FuseNavigationItem[]> {
    return this.venueService.getAllVenues().pipe(
      map((venues: any[]) => {
        const venueItems: FuseNavigationItem[] = venues.map((venue, index) => ({
          id: `venue-${index + 1}`,
          title: venue.name,
          type: 'basic',
          link: '', // empty for now
        }));

        // Create a deep copy of defaultNavigation
        const nav = JSON.parse(JSON.stringify(defaultNavigation));
        const venuesNav = nav[0].children?.find(item => item.id === 'venues');
        
        if (venuesNav) {
          venuesNav.children = venueItems;
        }

        return nav;
      })
    );
  }

  // Optional: Method to refresh navigation if user changes
  refreshNavigation(): void {
    this.loadNavigationBasedOnRole();
  }
}
